import {
    Card,
    CardActions,
    CardContent,
    CardHeader,
    Divider,
    Grid,
    Typography,
} from '@mui/material';
import React from 'react';
import {
    AutocompleteElement,
    TextFieldElement,
    useFieldArray,
    useFormContext,
    useWatch,
} from 'react-hook-form-mui';
import { useWebPushSites } from '../../../sic-lib/api/hooks/useWebPush';
import { WebPushSendRequest } from '../../../sic-lib/api/webPush/types/webPush';
import { webPushMessageFormDefaults } from '../../../sic-lib/defaults';
import ButtonLoading from '../../Shared/ButtonLoading';
import WebPushCapabilities from './WebPushCapabilities';
import { WebPushNotification } from './WebPushNotification';

interface Props {
    isLoading: boolean;
    isSuccess: boolean;
    isError: boolean;
}

export function MessagingControls({ isLoading, isSuccess, isError }: Props) {
    const { fields, append, remove } = useFieldArray({
        name: 'actions',
    });

    const formState = useWatch() as WebPushSendRequest;

    const { setValue, formState: isFormState, reset } = useFormContext();

    const {
        data: webPushSitesDropdown,
        isLoading: isDropdownLoading,
        isSuccess: isDropdownSuccess,
    } = useWebPushSites();

    React.useEffect(() => {
        setValue('url', null);
    }, [formState.domainInfo]);

    React.useEffect(() => {
        if (isFormState.isSubmitSuccessful) {
            reset(webPushMessageFormDefaults);
        }
    }, [isFormState.isSubmitSuccessful, reset]);

    return (
        <Grid container spacing={2}>
            <Grid item xs={true}>
                <Card>
                    <CardHeader title="Web Push" />
                    <CardContent>
                        <Grid container spacing={1} flexDirection="column">
                            <Grid item>
                                <AutocompleteElement
                                    name="domainInfo"
                                    label="Domain Name"
                                    options={webPushSitesDropdown || []}
                                    loading={isDropdownLoading}
                                    matchId={true}
                                    autocompleteProps={{
                                        disableClearable: true,
                                    }}
                                    required
                                />
                            </Grid>
                            {+formState.domainInfo > 0 && (
                                <WebPushCapabilities
                                    siteId={+formState.domainInfo}
                                    url={formState.url || ''}
                                />
                            )}
                            {+formState.domainInfo < 0 && (
                                <Grid item>
                                    <TextFieldElement
                                        name="url"
                                        label={
                                            `${
                                                webPushSitesDropdown
                                                    ?.find(itm => itm.id === formState.domainInfo)
                                                    ?.label.split('(External)')[0]
                                            }/` || ''
                                        }
                                        fullWidth
                                        required
                                    />
                                </Grid>
                            )}
                            <Grid item>
                                <TextFieldElement
                                    name="header"
                                    label="Header"
                                    fullWidth
                                    required
                                    validation={{
                                        maxLength: {
                                            value: 64,
                                            message: 'This Header is too long.',
                                        },
                                    }}
                                />
                            </Grid>
                            <Grid item>
                                <TextFieldElement
                                    name="body"
                                    label="Body"
                                    fullWidth
                                    required
                                    validation={{
                                        maxLength: {
                                            value: 120,
                                            message: 'This Body is too long.',
                                        },
                                    }}
                                />
                            </Grid>
                            <Grid item>
                                <TextFieldElement name="icon" label="Icon" fullWidth required />
                            </Grid>
                            <Grid item>
                                <Divider sx={{ mb: 2, mt: 2 }} />
                                <Typography variant="h6">Actions</Typography>
                                <Typography variant="body2">
                                    Chrome & Edge allow 2 actions/buttons to be shown and
                                    customized. All other browsers ignore these options.
                                </Typography>
                            </Grid>
                            <Grid container item spacing={2}>
                                {fields.map((field, index) => (
                                    <Grid item xs={true} key={field.id}>
                                        <TextFieldElement
                                            name={`actions[${index}].action`}
                                            label="Action"
                                            fullWidth
                                            style={{ marginBottom: 8 }}
                                            required
                                        />
                                        <TextFieldElement
                                            name={`actions[${index}].title`}
                                            label="Title"
                                            fullWidth
                                            style={{ marginBottom: 8 }}
                                            required
                                        />
                                        <TextFieldElement
                                            name={`actions[${index}].url`}
                                            label="URL"
                                            fullWidth
                                            required
                                        />
                                    </Grid>
                                ))}
                            </Grid>
                        </Grid>
                    </CardContent>
                    <CardActions>
                        <ButtonLoading
                            type="submit"
                            variant="contained"
                            color="primary"
                            isLoading={isLoading}
                            isSuccess={isSuccess}
                            successLabel="Message Broadcasted"
                            isError={isError}
                        >
                            Broadcast Message
                        </ButtonLoading>
                    </CardActions>
                </Card>
            </Grid>
            <Grid item>
                <WebPushNotification
                    iconSrc="https://picsum.photos/200"
                    title={formState.header}
                    message={formState.body}
                    website={formState.domainInfo}
                    onClose={() => {}}
                    siteItems={webPushSitesDropdown || []}
                    buttons={formState.actions?.map(x => x.title || '')}
                />
            </Grid>
        </Grid>
    );
}
